import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Camera, Trash2, Image as ImageIcon, Library } from 'lucide-react';
import api from '../api/client';
import { toast } from 'sonner';
import ImageLibraryPicker from './ImageLibraryPicker';

/**
 * ThumbnailEditor - A component for viewing and editing thumbnails
 * Used in right panels and edit dialogs for courses and labs
 */
const ThumbnailEditor = ({
  thumbnailUrl,
  onThumbnailChange,
  orgId,
  itemId,
  itemType = 'course', // 'course', 'lab', 'industry-lab'
  className = ''
}) => {
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  
  // Cropping state
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 100, aspect: 16 / 9 });
  const [completedCrop, setCompletedCrop] = useState(null);

  // Handle file selection - opens crop dialog
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Error', { description: 'Please select an image file' });
      return;
    }
    
    // Validate file size (max 10MB for pre-crop)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Error', { description: 'Image must be less than 10MB' });
      return;
    }
    
    // Create preview URL and open crop dialog
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setSelectedFile(file);
      setShowCropDialog(true);
      // Reset crop to default 16:9
      setCrop({ unit: '%', width: 100, aspect: 16 / 9 });
      setCompletedCrop(null);
    };
    reader.readAsDataURL(file);
    
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Handle image load in crop dialog - auto-complete the initial crop
  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    // Calculate the maximum crop area with 16:9 aspect ratio
    const aspect = 16 / 9;
    let cropWidth = width;
    let cropHeight = width / aspect;
    
    if (cropHeight > height) {
      cropHeight = height;
      cropWidth = height * aspect;
    }
    
    const x = (width - cropWidth) / 2;
    const y = (height - cropHeight) / 2;
    
    // Set initial completed crop in pixels
    setCompletedCrop({
      unit: 'px',
      x,
      y,
      width: cropWidth,
      height: cropHeight
    });
    
    setCrop({
      unit: 'px',
      x,
      y,
      width: cropWidth,
      height: cropHeight,
      aspect: 16 / 9
    });
  }, []);

  // Generate cropped image blob
  const getCroppedImg = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!imgRef.current) {
        reject(new Error('Image not loaded'));
        return;
      }
      
      if (!completedCrop || completedCrop.width <= 0 || completedCrop.height <= 0) {
        reject(new Error('Please adjust the crop area'));
        return;
      }

      const image = imgRef.current;
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      
      // Set canvas to desired output size (max 1920x1080 for 16:9)
      const maxWidth = 1920;
      const maxHeight = 1080;
      
      const cropWidth = completedCrop.width * scaleX;
      const cropHeight = completedCrop.height * scaleY;
      
      // Ensure we have valid dimensions
      if (cropWidth <= 0 || cropHeight <= 0 || isNaN(cropWidth) || isNaN(cropHeight)) {
        reject(new Error('Invalid crop dimensions'));
        return;
      }
      
      let outputWidth = Math.round(cropWidth);
      let outputHeight = Math.round(cropHeight);
      
      if (outputWidth > maxWidth) {
        const ratio = maxWidth / outputWidth;
        outputWidth = maxWidth;
        outputHeight = Math.round(outputHeight * ratio);
      }
      if (outputHeight > maxHeight) {
        const ratio = maxHeight / outputHeight;
        outputHeight = maxHeight;
        outputWidth = Math.round(outputWidth * ratio);
      }
      
      // Ensure minimum dimensions
      outputWidth = Math.max(1, outputWidth);
      outputHeight = Math.max(1, outputHeight);
      
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        cropWidth,
        cropHeight,
        0,
        0,
        outputWidth,
        outputHeight
      );

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create image. Please try a different image.'));
          }
        },
        'image/jpeg',
        0.9
      );
    });
  }, [completedCrop]);

  // Upload cropped image
  const handleCropAndUpload = async () => {
    if (!completedCrop) {
      toast.error('Error', { description: 'Please select a crop area' });
      return;
    }
    
    if (!orgId) {
      toast.error('Error', { description: 'Organization ID is missing' });
      return;
    }
    
    if (!itemId) {
      toast.error('Error', { description: 'Item ID is missing' });
      return;
    }
    
    setUploading(true);
    try {
      const croppedBlob = await getCroppedImg();
      
      // Create form data with cropped image
      const formData = new FormData();
      formData.append('file', croppedBlob, selectedFile?.name || 'thumbnail.jpg');
      formData.append('type', 'thumbnail');
      
      // Don't set Content-Type header - let browser set it with boundary
      const uploadResponse = await api.post(`/organizations/${orgId}/upload`, formData);
      
      const newThumbnailUrl = uploadResponse.data?.url || uploadResponse.data?.fileUrl;
      
      if (!newThumbnailUrl) {
        throw new Error('No thumbnail URL returned from server');
      }
      
      if (onThumbnailChange) {
        await onThumbnailChange(itemId, newThumbnailUrl);
      }
      
      toast.success('Success', { description: 'Thumbnail updated!' });
      setShowCropDialog(false);
      setImageSrc(null);
      setSelectedFile(null);
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.message || 'Failed to upload image';
      toast.error('Error', { description: errorMessage });
    } finally {
      setUploading(false);
    }
  };

  // Remove thumbnail
  const handleRemoveThumbnail = async () => {
    if (!onThumbnailChange) return;
    
    try {
      await onThumbnailChange(itemId, null);
      toast.success('Success', { description: 'Thumbnail removed' });
    } catch (error) {
      toast.error('Error', { description: 'Failed to remove thumbnail' });
    }
  };

  return (
    <>
      <div className={`space-y-3 ${className}`}>
        <h4 className="text-sm font-semibold text-gray-700">Thumbnail</h4>
        
        {/* Thumbnail Preview */}
        <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt="Thumbnail"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
              <ImageIcon className="w-8 h-8 mb-2" />
              <span className="text-xs">No thumbnail</span>
            </div>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => fileInputRef.current?.click()}
          >
            <Camera className="w-4 h-4 mr-2" />
            {thumbnailUrl ? 'Change' : 'Upload'}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => setShowLibrary(true)}
          >
            <Library className="w-4 h-4 mr-2" />
            Library
          </Button>
          
          {thumbnailUrl && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={handleRemoveThumbnail}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>

      {/* Image Library Picker */}
      <ImageLibraryPicker
        open={showLibrary}
        onOpenChange={setShowLibrary}
        onSelectImage={async (imageUrl) => {
          if (onThumbnailChange) {
            await onThumbnailChange(itemId, imageUrl);
            toast.success('Success', { description: 'Thumbnail updated from library!' });
          }
        }}
        defaultCategory={
          itemType === 'industry-lab' ? 'retail' :
          itemType === 'lab' ? 'machine-learning' :
          'ai-courses'
        }
      />

      {/* Image Crop Dialog */}
      <Dialog open={showCropDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCropDialog(false);
          setImageSrc(null);
          setSelectedFile(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Crop Thumbnail Image</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Adjust the crop area to select the 16:9 portion of your image that will be displayed as the thumbnail.
            </p>
            
            {imageSrc && (
              <div className="flex justify-center bg-gray-100 rounded-lg p-4 max-h-[400px] overflow-auto">
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={16 / 9}
                  className="max-w-full"
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Crop preview"
                    style={{ maxHeight: '350px', maxWidth: '100%' }}
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
              </div>
            )}
            
            <p className="text-xs text-gray-400">
              Tip: Drag the corners to resize, or drag the crop area to reposition. The final image will be optimized for web display.
            </p>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCropDialog(false);
                setImageSrc(null);
                setSelectedFile(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCropAndUpload}
              disabled={uploading || !completedCrop}
            >
              {uploading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Uploading...
                </span>
              ) : (
                'Crop & Upload'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ThumbnailEditor;
